import React, { use, useEffect } from 'react';
import cn from 'clsx';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import ComparisonWidgetItem from '@components-clean/core/widgets/comparison-widget-item';
import WidgetItem from '@components-clean/core/widgets/studyDashboard-widget-item';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { FieldDefinitionDto } from '@interfaces-clean/field-rdl-definition.interface';
import { StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { useLocalState, useTranslation } from '@hooks/index';
import { useGetWidgets } from '@apis/bff-api/central-monitoring';

const WidgetsSection = () => {
    const { t }: { t: (key: string) => string } = useTranslation('cm');
    const { current } = use(StudySiteHubContext);
    const { sessionId: siteFilterSessionId, filtersChanged$ } = useStudyDataFilterContext();
    const { state } = useLocalState();

    const fieldDefinitionFields = (state?.cm2Fields as FieldDefinitionDto[])?.filter((field) => !field?.isHidden);
    const { data, isMutating, error, trigger } = useGetWidgets(
        current.studyRef,
        current.dataset.id,
        siteFilterSessionId,
        current.datasetFilters.comparisonDataset?.id ?? null,
    );
    const returnWidgetTitle = (fieldKey: string | null | undefined) => {
        const currentFieldDefinition = fieldDefinitionFields?.find(
            (fieldDefinitionField) => fieldDefinitionField.key === fieldKey,
        );

        return currentFieldDefinition?.name;
    };

    useEffect(() => {
        const triggerRequest = () => {
            if (!siteFilterSessionId) {
                return;
            }
            trigger().catch((err) => window.console.log(err));
        };
        triggerRequest();

        // Subscribe to the filter changed event emitted by the Filter UI, and re-trigger the get
        const subscription = filtersChanged$.subscribe(() => {
            triggerRequest();
        });

        // Return a function that unsubscribes from the observable
        return () => subscription.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [current.studyRef, current.dataset.id, siteFilterSessionId, current.datasetFilters.comparisonDataset?.id]);

    const componentClassName = 'widget-section';
    const componentTestId = 'widgets-section';

    if (error || isMutating) {
        return (
            <Card
                title={t('studyHubPage.widgets')}
                className={cn(componentClassName, 'empty')}
                data-testid={componentTestId}
                style={{ marginBlock: 6, height: 180 }}
            >
                {error ? <ErrorMessage /> : <Loading />}
            </Card>
        );
    }

    let content;

    const isEmpty = !data || data.length === 0;

    if (!isEmpty) {
        const showCompaisonWidgets = data[0].comparisonDatasetSelected;

        content = showCompaisonWidgets
            ? data.map((widget) => {
                  return (
                      <ComparisonWidgetItem
                          key={widget.id}
                          title={widget.displayName}
                          titleTooltip={returnWidgetTitle(widget.fieldKey)}
                          isDraggable={false}
                          onClose={() => {}}
                          data-testid={`widget-${widget.id}`}
                          datasetInFocusScore={widget.value}
                          comparisonDatasetScore={widget.comparisonDatasetValue}
                          comparisonDatasetSelected={widget.comparisonDatasetSelected}
                          change={widget.change}
                      />
                  );
              })
            : data.map((widget) => {
                  return (
                      <WidgetItem
                          key={widget.id}
                          title={widget.displayName}
                          titleTooltip={returnWidgetTitle(widget.fieldKey)}
                          subtitle={widget.value?.toString() ?? t('componentsClean.widgets.noDataToDisplay')}
                          isDraggable={false}
                          icon={widget.iconName}
                          data-testid={`widget-${widget.id}`}
                          isActiveItem={false}
                          onClose={() => {}}
                      />
                  );
              });
    }

    return (
        <div className={cn(componentClassName, { empty: isEmpty })} data-testid={componentTestId}>
            {content}
        </div>
    );
};

export default WidgetsSection;
